<template>
  <PreAuthPage>
    <div class="sh-card__head" data-testid="onboard-start-page">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <h1 class="sh-card__title">{{ title }}</h1>
      <p v-if="preview && preview.position" class="sh-card__subtitle onboard-start__position" data-testid="onboard-start-position">{{ preview.position }}</p>
      <p class="sh-card__subtitle">{{ subtitle }}</p>
    </div>

    <!-- 🔴 One sentence for every way a link can be no good, so nothing about the person or the deal leaks. -->
    <p v-if="errorMessage" class="sh-card__error" role="alert" data-testid="onboard-start-error">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ errorMessage }}</span>
    </p>

    <!-- D161d: switched off — said plainly, and the button is not there to press. -->
    <p v-if="mode === 'ready' && preview && !preview.mailEnabled" class="onboard-start__note" role="status" data-testid="onboard-start-mail-off">
      {{ t("onboardStart.mailOff") }}
    </p>

    <v-btn
      v-if="(mode === 'ready' || mode === 'sending') && preview && preview.mailEnabled"
      color="primary"
      variant="flat"
      size="large"
      block
      :loading="mode === 'sending'"
      data-testid="onboard-start-send"
      @click="send()"
      >{{ t("onboardStart.send") }}</v-btn
    >

    <!-- No signal, or a busy/failing server: the link is probably fine, so say "try again", never "not valid". -->
    <v-btn v-if="mode === 'unreachable'" color="primary" variant="flat" size="large" block data-testid="onboard-start-retry" @click="load()">{{ t("onboardStart.retry") }}</v-btn>

    <p v-if="mode === 'sent'" class="onboard-start__note onboard-start__note--done" role="status" data-testid="onboard-start-sent">
      <v-icon size="18">mark_email_read</v-icon>
      <span>{{ t("onboardStart.sent", { email: maskedEmail }) }}</span>
    </p>
    <p v-if="refusal && (mode === 'ready' || mode === 'refused')" class="onboard-start__note" role="status" data-testid="onboard-start-refused">{{ t(`onboardStart.${refusal}`) }}</p>

    <!-- Somebody who can already sign in may simply do that — the sign-in page is not a secret. -->
    <v-btn v-if="preview && preview.canSignIn && mode !== 'invalid'" variant="text" size="large" block class="onboard-start__sign-in" data-testid="onboard-start-sign-in" @click="toSignIn()">{{
      t("onboardStart.signIn")
    }}</v-btn>

    <p v-if="preview && mode !== 'invalid'" class="sh-card__footnote" data-testid="onboard-start-footnote">{{ t("onboardStart.footnote") }}</p>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { readStart, startOnboarding, type StartPreview, type StartRefusal } from "@/auth/onboardingStart";
import { rememberReturnTo } from "@/auth/sessionGone";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import { APP_BRANDING, APP_NAME } from "@/app";

type Mode = "loading" | "invalid" | "unreachable" | "ready" | "sending" | "sent" | "refused";

/** Where an onboarding lives once the person is in (D161) — the same place `Invitation.vue` sends them. */
const ONBOARDING_RETURN = { pathname: "/profile", search: "?section=onboarding" } as const;

/**
 * 🔴 **THE FAR END OF AN ONBOARDING START LINK** (PNK-253) — `/onboard/<token>`, from a QR on the Desk, a copied link
 * or a WhatsApp message. No sign-in needed, and none given: the page names the production and the position, shows the
 * person's address MASKED, and has one button — "Email me my onboarding link" — which asks Pinksy to email the person
 * their own link, to the address on record. The link that mail carries is never on this page.
 */
export default defineComponent({
  name: "OnboardingStart",
  components: { LogoTile, PreAuthPage },
  props: {
    /** The token out of `/onboard/<token>`. Read by App.vue so this view never parses a URL. */
    token: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();
    const tenantName = APP_NAME;
    const branding = APP_BRANDING;

    const preview = ref<StartPreview | null>(null);
    const mode = ref<Mode>("loading");
    const errorMessage = ref<string | null>(null);
    const refusal = ref<StartRefusal | null>(null);
    const maskedEmail = ref("");

    const title = computed(() => (preview.value === null || preview.value.productionTitle === "" ? t("onboardStart.titleBare") : t("onboardStart.title", { production: preview.value.productionTitle })));
    const subtitle = computed(() => {
      if (mode.value === "loading") return t("onboardStart.loading");
      if (mode.value === "invalid") return t("onboardStart.invalidHint");
      if (mode.value === "unreachable") return t("onboardStart.unreachableHint");
      return t("onboardStart.intro", { email: maskedEmail.value });
    });

    async function load(): Promise<void> {
      mode.value = "loading";
      errorMessage.value = null;
      const outcome = await readStart(props.token);
      if (!outcome.ok) {
        errorMessage.value = outcome.message ?? (outcome.retry ? t("onboardStart.unreachable") : t("onboardStart.invalid"));
        mode.value = outcome.retry ? "unreachable" : "invalid";
        return;
      }
      preview.value = outcome.preview;
      maskedEmail.value = outcome.preview.maskedEmail;
      mode.value = "ready";
    }

    onMounted(load);

    async function send(): Promise<void> {
      if (mode.value !== "ready") return;
      mode.value = "sending";
      refusal.value = null;
      errorMessage.value = null;
      const outcome = await startOnboarding(props.token);
      if (!outcome.ok) {
        errorMessage.value = outcome.message ?? (outcome.retry ? t("onboardStart.unreachable") : t("onboardStart.invalid"));
        // A press that never arrived, or a busy server, leaves the button there to press again.
        mode.value = outcome.retry ? "ready" : "invalid";
        return;
      }
      if (outcome.result.maskedEmail !== "") maskedEmail.value = outcome.result.maskedEmail;
      if (outcome.result.sent) {
        mode.value = "sent";
        return;
      }
      refusal.value = outcome.result.reason ?? "unavailable";
      // A busy moment or a mail that did not go can be tried again in a while; the rest cannot from here.
      mode.value = refusal.value === "throttled" || refusal.value === "notDelivered" ? "ready" : "refused";
    }

    /** Remember their onboarding, then a full load at "/" — the shell's own sign-in, and back to it once they are in. */
    function toSignIn(): void {
      rememberReturnTo(ONBOARDING_RETURN.pathname, ONBOARDING_RETURN.search);
      window.location.assign("/");
    }

    return { t, tenantName, branding, preview, mode, title, subtitle, errorMessage, refusal, maskedEmail, send, load, toSignIn };
  },
});
</script>

<style scoped>
.onboard-start__position {
  font-weight: 600;
  color: var(--sh-text);
}
.onboard-start__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sh-text-60);
}
.onboard-start__note--done {
  color: rgb(var(--v-theme-success));
}
.onboard-start__sign-in {
  margin-top: 8px;
}
</style>
